import { useContext, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'preact/hooks';
import { UploadIcon } from '../ecardsPreact.icons/UploadIcon';
import { Steps } from '../ecardsPreact.constants/steps';
import { ECardGlobalState } from './ECardGlobalState';
import { FieldError } from './FieldError';
import { useField } from 'formik';
import eCardRecipient from '../ecardsPreact.classes/eCardRecipient';
import Papa from 'papaparse';

export default function ECardAddRecipientsCsv(props) {
    const { currentStep, hidden, maxRecipients } = props;

    const [recipientsField, meta, helpers] = useField('EcardMessageDetails.Recipients');

    const [fileName, setFileName] = useState(null);
    const [recipients, setRecipients] = useState([]);
    const [touched, setTouched] = useState(false);
    const [fileInfo, setFileInfo] = useState();

    const fileInput = useRef(null);
    const prevStepRef = useRef();
    const prevStep = prevStepRef.current;

    const { isValidCsv, recipientsCsvError } = useContext(ECardGlobalState);

    const processData = (data) => {
        let validRecipients = [];

        data.forEach((row) => {
            if (row.length == 2) {
                const emailIndex = row.findIndex((element) => element.includes('@'));

                // Email address should always be on the second column
                if (emailIndex > -1 && emailIndex === 1) {
                    const name = row[0];
                    const email = row[1];

                    if (email.length && name.length) {
                        validRecipients.push(new eCardRecipient(name, email));
                    }
                }
            }
        });

        if (validRecipients.length) {
            const currentRecipients = recipientsField.value.filter((recipient) => recipient.Name.length && recipient.Email.length);

            if (validRecipients.length + currentRecipients.length > maxRecipients) {
                setFileInfo(null);
                recipientsCsvError.set(
                    `You can only add a total of ${maxRecipients} recipients, please remove ${
                        validRecipients.length + currentRecipients.length - maxRecipients
                    } recipients and try again`
                );
            } else {
                const recipients = [...currentRecipients, ...validRecipients];
                const uniqueRecipients = recipients.filter((recipient, index) => recipients.findIndex((r) => r.Email === recipient.Email) === index);
                const duplicates = recipients.length - uniqueRecipients.length;
                setRecipients(uniqueRecipients);
                setFileInfo(
                    `${validRecipients.length} recipients will be added.${
                        duplicates > 0 ? ` ${duplicates} entries with the duplicate email addresses have been removed.` : ''
                    }`
                );
                isValidCsv.set(true);
            }
        } else {
            setFileInfo(null);
            recipientsCsvError.set('Please upload a valid CSV that matches the example provided');
        }
    };

    const getData = (results) => {
        if (results.data.length) {
            processData(results.data);
        } else {
            setFileInfo(null);
            recipientsCsvError.set('Please upload a valid CSV that matches the example provided');
        }

        fileInput.current.value = null;
    };

    const handleFileClick = (event) => {
        fileInput.current.click();
        setTouched(true);
    };

    const handleFileChange = (event) => {
        recipientsCsvError.set(null);
        const files = event.target.files;
        if (files.length) {
            const file = files[0];
            const fileName = file.name;
            const extension = fileName?.split('.').pop().toLowerCase();

            setFileName(fileName);

            if (extension === 'csv') {
                Papa.parse(file, {
                    complete: getData,
                });
            } else {
                recipientsCsvError.set('Please upload a valid file type');
            }
        }
    };

    useEffect(() => {
        if (recipientsCsvError.get?.length) {
            setTouched(true);
            isValidCsv.set(false);
        }
    }, [recipientsCsvError.get]);

    useLayoutEffect(
        (e) => {
            if (prevStep === Steps.AddRecipientsCsv && currentStep === Steps.Personalise) {
                if (recipients.length) {
                    helpers.setValue(recipients);
                }
            } else if (currentStep === Steps.AddRecipientsCsv) {
                setRecipients([]);
                setFileName(null);
                setFileInfo(null);
                recipientsCsvError.set(null);
                isValidCsv.set(false);
            }

            prevStepRef.current = currentStep;
        },
        [currentStep]
    );

    return (
        <section class="ecard-form__section" hidden={hidden} aria-labelledby="recipients-manual">
            <header class="ecard-form__header">
                <h1 class="text-h3 text-blue" id="recipients-manual">
                    Upload a CSV
                </h1>
                <div class="rich-text pt-2 lg:pt-8">
                    <p>Please select a CSV containing your recipients names and email addresses in the field below.</p>
                    <p>The first column should contain the recipient name, and the second should contain a their valid email address. </p>
                    <p>
                        Alternatively you can&nbsp;
                        <a class="text-button text-blue" href="/Areas/Ecards/Content/Files/ecard-recipients-example.csv" download>
                            download this example file
                        </a>
                        . When saving your changes, make sure the file is saved as a CSV.
                    </p>
                </div>
            </header>

            <div class="file-selector__wrap">
                <button class={`file-selector ${fileName ? '' : 'file-selector--placeholder'}`} type="button" onClick={handleFileClick}>
                    <div class="file-selector__text">{fileName || 'Please select a CSV'}</div>
                    <div class="file-selector__button">
                        <UploadIcon class="file-selector__icon" />
                    </div>
                </button>
                <input ref={fileInput} type="file" accept=".csv" onChange={handleFileChange} class="sr-only" />
                <FieldError error={recipientsCsvError.get} touched={touched} className="text-center" />
                {fileInfo && <div className={`field-info`}>{fileInfo}</div>}
            </div>
        </section>
    );
}
